/**
 * 현장 맵 캔버스 — offline-manage master-console 의 map.js 렌더를 React 로 옮긴 것.
 * 도형·구역 라벨·CCTV 마커를 그리고, 휠 줌·드래그 팬·마커 hover(담당 주차면 강조)·
 * 마커 클릭(onCctvClick)을 지원한다. 선택된 CCTV 는 흰 이중 링으로 표시한다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import {
  buildFloorModel,
  centroid,
  computeBase,
  type FitBase,
  type SiteMapCamera,
  type SiteMapLevel,
} from './siteMapModel';

const MARKER_R = 6;
const HOVER_R = 12;
const ZOOM_STEP = 1.25;
const ZOOM_MIN = 0.4;
const ZOOM_MAX = 12;
const LABEL_COLOR = '#ef4444';
const CCTV_LABEL_COLOR = '#dbe4f0';
const CAM_COLOR = { light: '#37d39b', warning: '#d8a23a', noip: '#7c8699' };
const REL_SLOT_FILL = 'rgba(45,212,191,0.30)';
const REL_SLOT_LINE = 'rgba(45,212,191,0.95)';

export interface SiteMapCanvasProps {
  levels: SiteMapLevel[];
  /** 강조할 CCTV 이름 */
  selectedCctv?: string;
  onCctvClick?: (cam: SiteMapCamera, floor: string) => void;
  height?: number | string;
  showCctvLabels?: boolean;
  /** CCTV↔주차면 관계선을 항상 옅게 그릴지(hover/선택은 이 값과 무관하게 강조). */
  showRelations?: boolean;
}

export default function SiteMapCanvas({
  levels,
  selectedCctv,
  onCctvClick,
  height = 420,
  showCctvLabels = true,
  showRelations = true,
}: SiteMapCanvasProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [floorIdx, setFloorIdx] = useState(0);
  const [view, setView] = useState({ zoom: 1, panX: 0, panY: 0 });
  const [hoverCam, setHoverCam] = useState<SiteMapCamera | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const downPtRef = useRef<{ x: number; y: number } | null>(null);
  const baseRef = useRef<FitBase | null>(null);

  const safeIdx = Math.min(floorIdx, Math.max(0, levels.length - 1));
  const model = useMemo(() => buildFloorModel(levels[safeIdx]?.mapData), [levels, safeIdx]);

  // 선택된 CCTV 가 다른 층에 있으면 그 층으로 따라간다.
  useEffect(() => {
    if (!selectedCctv) return;
    const idx = levels.findIndex((lvl) => buildFloorModel(lvl.mapData).cameras.some((c) => c.name === selectedCctv));
    if (idx >= 0 && idx !== safeIdx) setFloorIdx(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCctv, levels]);

  const cssSize = () => {
    const r = stageRef.current?.getBoundingClientRect();
    return { width: Math.max(1, r?.width || 1), height: Math.max(1, r?.height || 1) };
  };

  const toCanvas = useCallback(
    (x: number, y: number): [number, number] => {
      const b = baseRef.current;
      if (!b) return [0, 0];
      return [(x * b.scale + b.ox) * view.zoom + view.panX, (y * b.scale + b.oy) * view.zoom + view.panY];
    },
    [view],
  );

  const cameraAt = useCallback(
    (px: number, py: number): SiteMapCamera | null => {
      if (!baseRef.current) return null;
      let best: SiteMapCamera | null = null;
      let bestD = HOVER_R * HOVER_R;
      for (const cam of model.cameras) {
        const [cx, cy] = toCanvas(cam.x, cam.y);
        const d = (cx - px) ** 2 + (cy - py) ** 2;
        if (d <= bestD) { bestD = d; best = cam; }
      }
      return best;
    },
    [model, toCanvas],
  );

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const { width, height: h } = cssSize();
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, h);

    baseRef.current = computeBase(model, width, h);
    if (!baseRef.current) return;

    ctx.lineWidth = 1;
    model.geom.forEach((g) => {
      ctx.beginPath();
      g.pts.forEach((p, i) => {
        const [cx, cy] = toCanvas(p[0], p[1]);
        if (i === 0) ctx.moveTo(cx, cy); else ctx.lineTo(cx, cy);
      });
      if (g.poly) {
        ctx.closePath();
        ctx.fillStyle = 'rgba(120,140,170,0.06)';
        ctx.strokeStyle = 'rgba(150,170,200,0.28)';
        ctx.fill();
      } else {
        ctx.strokeStyle = 'rgba(150,170,200,0.40)';
      }
      ctx.stroke();
    });

    // CCTV↔주차면 관계선 — 모든 카메라에 옅게, hover/선택 카메라는 진하게.
    const relName = hoverCam?.name || selectedCctv;
    if (showRelations) {
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(45,212,191,0.22)';
      model.cameras.forEach((cam) => {
        if (cam.name === relName) return;
        (model.slotsByCam.get(cam.name) || []).forEach((pts) => {
          const c0 = centroid(pts);
          if (!c0) return;
          const [ax, ay] = toCanvas(cam.x, cam.y);
          const [bx, by] = toCanvas(c0[0], c0[1]);
          ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
        });
      });
    }
    const relPts = relName ? model.slotsByCam.get(relName) : undefined;
    const relCam = relName ? model.cameras.find((cam) => cam.name === relName) : undefined;
    if (relPts && relCam) {
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = REL_SLOT_LINE;
      relPts.forEach((pts) => {
        const c0 = centroid(pts);
        if (!c0) return;
        const [ax, ay] = toCanvas(relCam.x, relCam.y);
        const [bx, by] = toCanvas(c0[0], c0[1]);
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
      });
    }
    if (relPts) {
      ctx.lineWidth = 1.5;
      relPts.forEach((pts) => {
        ctx.beginPath();
        pts.forEach((p, i) => {
          const [cx, cy] = toCanvas(p[0], p[1]);
          if (i === 0) ctx.moveTo(cx, cy); else ctx.lineTo(cx, cy);
        });
        ctx.closePath();
        ctx.fillStyle = REL_SLOT_FILL;
        ctx.strokeStyle = REL_SLOT_LINE;
        ctx.fill();
        ctx.stroke();
      });
      ctx.lineWidth = 1;
    }

    model.cameras.forEach((cam) => {
      const [cx, cy] = toCanvas(cam.x, cam.y);
      const color = cam.ip ? CAM_COLOR[cam.kind] : CAM_COLOR.noip;
      if (cam.name === selectedCctv) {
        ctx.beginPath(); ctx.arc(cx, cy, MARKER_R + 9, 0, Math.PI * 2);
        ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(255,255,255,0.95)'; ctx.stroke();
        ctx.beginPath(); ctx.arc(cx, cy, MARKER_R + 15, 0, Math.PI * 2);
        ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(255,255,255,0.45)'; ctx.stroke();
        ctx.lineWidth = 1;
      }
      ctx.beginPath();
      ctx.arc(cx, cy, MARKER_R, 0, Math.PI * 2);
      ctx.fillStyle = color; ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(10,16,24,0.85)';
      ctx.stroke();
      if (cam.kind === 'warning') {
        ctx.beginPath();
        ctx.moveTo(cx, cy - MARKER_R - 4);
        ctx.lineTo(cx - 3.5, cy - MARKER_R + 1);
        ctx.lineTo(cx + 3.5, cy - MARKER_R + 1);
        ctx.closePath();
        ctx.fillStyle = color; ctx.fill();
      }
    });

    if (showCctvLabels) {
      ctx.save();
      ctx.font = "600 10px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 3;
      model.cameras.forEach((cam) => {
        const lines = [cam.name, cam.ip && cam.ip !== cam.name ? cam.ip : ''].filter(Boolean);
        const [cx, cy] = toCanvas(cam.x, cam.y);
        let ly = cy + MARKER_R + 6;
        lines.forEach((text) => {
          ctx.strokeStyle = 'rgba(8,12,20,0.82)';
          ctx.strokeText(text, cx, ly);
          ctx.fillStyle = CCTV_LABEL_COLOR;
          ctx.fillText(text, cx, ly);
          ly += 11;
        });
      });
      ctx.restore();
    }

    if (model.labels.length) {
      ctx.save();
      ctx.font = "600 12px ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineJoin = 'round';
      model.labels.forEach((l) => {
        const [cx, cy] = toCanvas(l.x, l.y);
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(8,12,20,0.78)';
        ctx.strokeText(l.text, cx, cy);
        ctx.fillStyle = LABEL_COLOR;
        ctx.fillText(l.text, cx, cy);
      });
      ctx.restore();
    }
  }, [model, toCanvas, hoverCam, selectedCctv, showCctvLabels, showRelations]);

  useEffect(() => {
    render();
    const stage = stageRef.current;
    if (!stage) return;
    const ro = new ResizeObserver(() => render());
    ro.observe(stage);
    return () => ro.disconnect();
  }, [render]);

  const zoomAt = (fx: number, fy: number, factor: number) => {
    setView((v) => {
      const z1 = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.zoom * factor));
      if (z1 === v.zoom) return v;
      const ratio = z1 / v.zoom;
      return { zoom: z1, panX: fx - (fx - v.panX) * ratio, panY: fy - (fy - v.panY) * ratio };
    });
  };
  const zoomCenter = (factor: number) => {
    const { width, height: h } = cssSize();
    zoomAt(width / 2, h / 2, factor);
  };

  // wheel 은 passive 기본이라 React onWheel 로는 preventDefault 가 안 먹는다.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      zoomAt(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP);
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
    // 층 데이터가 늦게 오면 캔버스가 나중에 마운트되므로 그때 다시 붙인다.
  }, [levels.length]);

  const localPt = (e: React.MouseEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { px: e.clientX - r.left, py: e.clientY - r.top, w: r.width, h: r.height };
  };

  const onMouseDown = (e: React.MouseEvent) => {
    const { px, py } = localPt(e);
    downPtRef.current = { x: e.clientX, y: e.clientY };
    if (cameraAt(px, py)) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: view.panX, panY: view.panY };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const { px, py, w, h } = localPt(e);
    const d = dragRef.current;
    if (d) {
      setView((v) => ({ ...v, panX: d.panX + (e.clientX - d.startX), panY: d.panY + (e.clientY - d.startY) }));
      setTooltipPos(null);
      return;
    }
    const cam = cameraAt(px, py);
    if (cam?.name !== hoverCam?.name) setHoverCam(cam);
    setTooltipPos(cam ? { x: Math.min(px + 14, w - 160), y: Math.min(py + 14, h - 70) } : null);
  };
  const endDrag = () => { dragRef.current = null; };
  const onMouseLeave = () => { endDrag(); setHoverCam(null); setTooltipPos(null); };
  const onClick = (e: React.MouseEvent) => {
    const dp = downPtRef.current;
    downPtRef.current = null;
    if (dp && (e.clientX - dp.x) ** 2 + (e.clientY - dp.y) ** 2 > 25) return; // 팬 드래그의 끝은 클릭이 아니다
    const { px, py } = localPt(e);
    const cam = cameraAt(px, py);
    if (cam && onCctvClick) onCctvClick(cam, levels[safeIdx]?.name || '');
  };

  if (levels.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">맵을 선택하면 도면이 표시됩니다.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
        <Tabs
          value={safeIdx}
          onChange={(_, v) => { setFloorIdx(v); setView({ zoom: 1, panX: 0, panY: 0 }); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 32, flex: 1, '& .MuiTab-root': { minHeight: 32, py: 0 } }}
        >
          {levels.map((lvl, i) => <Tab key={`${lvl.name}-${i}`} label={lvl.name} />)}
        </Tabs>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          CCTV {model.cameras.length}대
        </Typography>
        <Tooltip title="확대"><IconButton size="small" onClick={() => zoomCenter(ZOOM_STEP)}><AddIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="축소"><IconButton size="small" onClick={() => zoomCenter(1 / ZOOM_STEP)}><RemoveIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="맞춤"><IconButton size="small" onClick={() => setView({ zoom: 1, panX: 0, panY: 0 })}><CenterFocusStrongIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
      <Box ref={stageRef} sx={{ position: 'relative', flex: 1, minHeight: 0, bgcolor: '#0f1620', borderRadius: 1, overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          style={{ display: 'block', cursor: dragRef.current ? 'grabbing' : hoverCam ? 'pointer' : 'default' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
        />
        {hoverCam && tooltipPos && (
          <Box
            sx={{
              position: 'absolute', left: tooltipPos.x, top: tooltipPos.y, pointerEvents: 'none',
              bgcolor: 'rgba(15,22,32,0.94)', color: '#e6edf6', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 1, px: 1, py: 0.5, fontSize: 12, lineHeight: 1.4, minWidth: 140,
            }}
          >
            <b>{hoverCam.name}</b>
            <div>{hoverCam.ip || '—'}</div>
            <div>담당 주차면 {(model.slotsByCam.get(hoverCam.name) || []).length}면</div>
            <div style={{ opacity: 0.7 }}>{hoverCam.kind === 'warning' ? '단방향' : '조명형'}</div>
          </Box>
        )}
      </Box>
    </Box>
  );
}
